import {
  GoogleAuthProvider, isSignInWithEmailLink, onIdTokenChanged, sendSignInLinkToEmail, signInWithEmailLink, signInWithPopup, signOut as fbSignOut,
  type User,
} from 'firebase/auth'
import { doc, onSnapshot, updateDoc } from 'firebase/firestore'
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ADMIN_ROLE_ID, roleHas, type Permission, type RoleDoc, type StaffAccessDoc } from '@sonder/shared'
import { auth, db } from '@/lib/firebase'
import { HEARTBEAT_MS } from '@/ui/Activity'

export type Role = 'staff' | 'client'
export type StaffAccess = { roleId: string; roleName: string; admin: boolean; role: RoleDoc | null }
/** An admin signed in as this person ("Sign in as"). Read from claims only the server can set. */
export type Impersonation = { byUid: string; byEmail: string | null; firstVisit: boolean }
export type AuthState = {
  loading: boolean
  user: User | null
  role: Role | null
  /** The signed-in person's team access, read live from staffAccess + roles. Null for clients. */
  staff: StaffAccess | null
  impersonation: Impersonation | null
  /** Whether the person's role grants a permission. Server rules enforce the same check. */
  can: (p: Permission) => boolean
  /** Surface the blocking-function message ("hasn't been invited") to the sign-in page. */
  error: string | null
  signInWithGoogle: () => Promise<void>
  /** Email a magic link. `next` is the in-app path to return to once the link is followed (an invite, a deep link). */
  sendEmailLink: (email: string, next?: string) => Promise<void>
  /**
   * Finishes a magic-link sign-in. `status` is 'email-required' when the link was opened on another device; call again with the address.
   * `next` is where to go afterwards: the path sendEmailLink was given, or '/'.
   */
  completeEmailLink: (email?: string) => Promise<EmailLinkResult>
  signOut: () => Promise<void>
  refreshClaims: () => Promise<void>
}

export type EmailLinkResult = { status: boolean | 'email-required'; next: string }

const Ctx = createContext<AuthState | null>(null)
const EMAIL_KEY = 'sonder:emailForSignIn'
const NEXT_KEY = 'sonder:nextAfterSignIn'
let inflight: Promise<EmailLinkResult> | null = null

/** Only same-origin paths may be used as a return destination, so a crafted link cannot bounce people elsewhere. */
function safePath(value: string | null | undefined): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null
  if (value.startsWith('/auth/') || value === '/sign-in') return null
  return value
}

function readStorage(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function writeStorage(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    /* private mode */
  }
}

/**
 * Activity shown to staff ("Active now", "Last active 3 hr ago"). Best effort: a failed write must never get in the
 * way of using the portal. Rules refuse these writes during "Sign in as", so previewing as someone never counts as them.
 */
function touchUser(uid: string, fields: { lastSeenAt: number; lastSignInAt?: number }): void {
  updateDoc(doc(db, `users/${uid}`), fields).catch(() => {})
}

function friendly(e: unknown): string {
  const code = (e as { code?: string })?.code ?? ''
  const msg = (e as { message?: string })?.message ?? ''
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return ''
  // Blocking-function rejections arrive as internal errors whose message carries our text.
  if (/invited/i.test(msg)) return "This email hasn't been invited to a portal yet. Ask your Sonder contact to add you."
  if (code === 'auth/invalid-action-code' || code === 'auth/expired-action-code') return 'That sign-in link has expired. Request a new one.'
  if (/permission-denied|invited/i.test(msg)) return msg.replace(/^.*?:\s*/, '')
  return 'Sign-in failed. Try again, or use the other option.'
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Keyed by the email / role id they were read for, so a stale snapshot never counts as current.
  const [access, setAccess] = useState<{ email: string; doc: StaffAccessDoc | null } | null>(null)
  const [roleDoc, setRoleDoc] = useState<{ id: string; doc: RoleDoc | null } | null>(null)
  const [claims, setClaims] = useState<{ uid: string; impersonation: Impersonation | null } | null>(null)

  useEffect(() => {
    return onIdTokenChanged(auth, (u) => {
      setUser(u)
      setUserLoading(false)
      if (!u) return setClaims(null)
      // The cached token, no network. Keyed by uid so the previous person's claims never count as current.
      u.getIdTokenResult()
        .then(({ claims: c }) => {
          const by = typeof c.impersonatedBy === 'string' ? c.impersonatedBy : null
          const impersonation = by ? { byUid: by, byEmail: typeof c.impersonatorEmail === 'string' ? c.impersonatorEmail : null, firstVisit: c.firstVisit === true } : null
          setClaims({ uid: u.uid, impersonation })
        })
        .catch(() => setClaims({ uid: u.uid, impersonation: null }))
    })
  }, [])

  const email = user?.emailVerified ? (user.email?.toLowerCase() ?? null) : null
  useEffect(() => {
    if (!email) return
    return onSnapshot(
      doc(db, `staffAccess/${email}`),
      (snap) => setAccess({ email, doc: snap.exists() ? (snap.data() as StaffAccessDoc) : null }),
      () => setAccess({ email, doc: null }),
    )
  }, [email])

  const currentAccess = email && access?.email === email ? access.doc : null
  const roleId = currentAccess?.roleId ?? null
  useEffect(() => {
    if (!roleId || roleId === ADMIN_ROLE_ID) return
    return onSnapshot(
      doc(db, `roles/${roleId}`),
      (snap) => setRoleDoc({ id: roleId, doc: snap.exists() ? (snap.data() as RoleDoc) : null }),
      () => setRoleDoc({ id: roleId, doc: null }),
    )
  }, [roleId])

  const accessLoading = Boolean(email) && access?.email !== email
  const roleLoading = Boolean(roleId) && roleId !== ADMIN_ROLE_ID && roleDoc?.id !== roleId
  const claimsLoading = Boolean(user) && claims?.uid !== user?.uid
  const loading = userLoading || accessLoading || roleLoading || claimsLoading
  const impersonation = user && claims?.uid === user.uid ? claims.impersonation : null

  // While the portal is open and on screen, record that this person is still here (see HEARTBEAT_MS).
  const heartbeatUid = user && !claimsLoading && !impersonation ? user.uid : null
  useEffect(() => {
    if (!heartbeatUid) return
    let last = 0
    const beat = () => {
      const now = Date.now()
      // Flipping between tabs fires visibilitychange often; one write per 15 s is plenty.
      if (document.visibilityState !== 'visible' || now - last < 15_000) return
      last = now
      touchUser(heartbeatUid, { lastSeenAt: now })
    }
    beat()
    const t = window.setInterval(beat, HEARTBEAT_MS)
    document.addEventListener('visibilitychange', beat)
    return () => {
      window.clearInterval(t)
      document.removeEventListener('visibilitychange', beat)
    }
  }, [heartbeatUid])
  const currentRole = roleId && roleDoc?.id === roleId ? roleDoc.doc : null
  const staff = useMemo<StaffAccess | null>(() => {
    if (!roleId) return null
    const admin = roleId === ADMIN_ROLE_ID
    return { roleId, admin, role: currentRole, roleName: admin ? 'Admin' : (currentRole?.name ?? roleId) }
  }, [roleId, currentRole])
  const role: Role | null = user ? (staff ? 'staff' : 'client') : null
  const can = useCallback((p: Permission) => roleHas(staff?.roleId, staff?.role, p), [staff])

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    try {
      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: 'select_account' })
      const { user: u } = await signInWithPopup(auth, provider)
      const now = Date.now()
      touchUser(u.uid, { lastSeenAt: now, lastSignInAt: now })
    } catch (e) {
      const m = friendly(e)
      if (m) setError(m)
    }
  }, [])

  const sendEmailLink = useCallback(async (email: string, next?: string) => {
    setError(null)
    const returnTo = safePath(next) === '/' ? null : safePath(next)
    // The return path rides along in the continue URL (works on any device) and in storage (in case a handler drops it).
    const url = new URL('/auth/email-link', window.location.origin)
    if (returnTo) url.searchParams.set('next', returnTo)
    await sendSignInLinkToEmail(auth, email, { url: url.toString(), handleCodeInApp: true })
    writeStorage(EMAIL_KEY, email)
    writeStorage(NEXT_KEY, returnTo)
  }, [])

  const completeEmailLink = useCallback(async (typed?: string) => {
    if (inflight) return inflight
    inflight = (async (): Promise<EmailLinkResult> => {
    const href = window.location.href
    const next = safePath(new URL(href).searchParams.get('next')) ?? safePath(readStorage(NEXT_KEY)) ?? '/'
    if (!isSignInWithEmailLink(auth, href)) return { status: false, next }
    const email = typed?.trim() || readStorage(EMAIL_KEY)
    if (!email) return { status: 'email-required', next }
    try {
      const { user: u } = await signInWithEmailLink(auth, email, href)
      const now = Date.now()
      touchUser(u.uid, { lastSeenAt: now, lastSignInAt: now })
      writeStorage(EMAIL_KEY, null)
      writeStorage(NEXT_KEY, null)
      return { status: true, next }
    } catch (e) {
      setError(friendly(e))
      return { status: false, next }
    }
    })()
    try {
      return await inflight
    } finally {
      inflight = null
    }
  }, [])

  const signOut = useCallback(async () => {
    await fbSignOut(auth)
  }, [])
  const refreshClaims = useCallback(async () => {
    await auth.currentUser?.getIdToken(true)
  }, [])

  const value = useMemo<AuthState>(
    () => ({ loading, user, role, staff, impersonation, can, error, signInWithGoogle, sendEmailLink, completeEmailLink, signOut, refreshClaims }),
    [loading, user, role, staff, impersonation, can, error, signInWithGoogle, sendEmailLink, completeEmailLink, signOut, refreshClaims],
  )
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>')
  return ctx
}
